import { SceneRenderer } from "@cva/renderer";
import {
  type Box,
  boardBounds,
  boardRects,
  boundingRect,
  buildParentMap,
  docMmToPx,
  isImagePlaceholder,
  moveToPage,
  type Point,
  pageAtBoardPoint,
  pageSize,
  type Rect,
  rectsIntersect,
  rootOf,
  type SceneDocument,
  scaleContent,
  setWorldBox,
  unionRects,
  worldBox,
} from "@cva/shared";
import { ImagePlus } from "lucide-react";
import {
  memo,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/utils";
import { EditorCommentPins, useCommentUi } from "./Collaboration";
import { CanvasContextMenu } from "./ContextMenu";
import { CropOverlay } from "./CropOverlay";
import { collageSwapTarget, swapCellImages } from "./collage-swap";
import {
  acceptsImage,
  addAsset,
  addIcon,
  addPageAfterLast,
  ICON_DRAG_TYPE,
  movePageOnBoard,
  parseIconDrag,
  replaceImage,
  screenshotOf,
  selectionRect,
} from "./commands";
import { DrawLayer } from "./DrawTool";
import { useEnv } from "./env";
import { PeerCanvasLayer } from "./LivePeers";
import { useLiveSync } from "./live";
import { GuideControls, LayoutOverlay, Rulers } from "./Rulers";
import { SelectionOverlay } from "./SelectionOverlay";
import { collectTargets, type SnapTargets, snapPageRect, snapRect, snapValue } from "./snapping";
import {
  type EditorStore,
  isBoardView,
  pageOrigin,
  pagePan,
  useEditor,
  useEditorApi,
  usePagePan,
} from "./store";
import { TextEditor } from "./TextEditor";
import {
  angleFrom,
  HANDLE_DIRECTIONS,
  type Handle,
  keepsAspect,
  pointInBox,
  resizeBox,
  scaleRect,
  snapAngle,
} from "./transform";
import { useVisionMode, VisionOverlay, visionFilter } from "./VisionSimulation";
import { addClipFromAsset, videoIntoDevice, videoIntoPlaceholder } from "./video/commands";
import { useCaptions } from "./video/data";
import { useVideoApi } from "./video/store";
import {
  boxedMainClipAtPoint,
  FramingOverlay,
  overlayClipAtPoint,
  SafeZones,
  snapBoxToFrame,
  TrackClipBox,
  VideoPageRenderer,
} from "./video/VideoCanvas";
import { useViewPrefs } from "./view";

const SNAP_PX = 6;
const PAD = 48;

type Gesture =
  | { type: "pan"; start: Point; pan: Point }
  | {
      type: "move";
      start: Point;
      items: { id: string; box: Box }[];
      rect: Rect;
      targets: SnapTargets;
      moved: boolean;
      clickId: string | null;
    }
  | {
      type: "resize";
      handle: Handle;
      items: { id: string; box: Box }[];
      rect: Rect;
      targets: SnapTargets;
      single: boolean;
    }
  | { type: "rotate"; center: Point; startAngle: number; items: { id: string; box: Box }[] }
  /** A page dragged by its label on the board. */
  | {
      type: "page-move";
      index: number;
      start: Point;
      origin: Point;
      others: Rect[];
      moved: boolean;
    }
  | {
      type: "marquee";
      start: Point;
      current: Point;
      additive: boolean;
      base: string[];
      /** Clip of an upper video track under the start: a click without drag selects it. */
      trackClip?: string;
    }
  /** A clip of an upper video track (or a main track clip with a box), moved on the frame. */
  | {
      type: "track-move";
      track: "overlay" | "video";
      id: string;
      start: Point;
      box: Rect;
      moved: boolean;
    };

/** Restores an element and all its descendants from the gesture's start state. */
function resetSubtree(draft: SceneDocument, base: SceneDocument, id: string, depth = 0) {
  const original = base.elements[id];
  if (!original || depth > 50) return;
  draft.elements[id] = JSON.parse(JSON.stringify(original));
  if (original.type === "group") {
    for (const childId of original.children) resetSubtree(draft, base, childId, depth + 1);
  }
}

export function printMetrics(doc: ReturnType<EditorStore["getState"]>["doc"]) {
  const print = doc.format.print;
  if (!print) return null;
  return { bleedPx: docMmToPx(doc, print.bleedMm), safePx: docMmToPx(doc, print.safeMm) };
}

/**
 * Fits the page into the viewport; on a board all pages (`mode: "all"`, the default
 * there) or only the active one (`"page"`).
 */
export function fitView(
  store: EditorStore,
  viewport: { width: number; height: number },
  mode?: "all" | "page",
) {
  const { doc, pageIndex } = store.getState();
  const board = isBoardView(doc);
  const bleed = printMetrics(doc)?.bleedPx ?? 0;
  const origin = pageOrigin(doc, pageIndex);
  const size = pageSize(doc, doc.pages[pageIndex]);
  const area: Rect =
    board && (mode ?? "all") === "all"
      ? boardBounds(doc)
      : { x: origin.x, y: origin.y, width: size.width, height: size.height };
  const zoom = Math.min(
    (viewport.width - PAD * 2) / (area.width + bleed * 2),
    (viewport.height - PAD * 2) / (area.height + bleed * 2),
    4,
  );
  store.getState().setView({
    zoom,
    pan: {
      x: (viewport.width - area.width * zoom) / 2 - area.x * zoom,
      y: (viewport.height - area.height * zoom) / 2 - area.y * zoom,
    },
  });
}

/** Brings the active page into view without changing the zoom more than needed. */
function focusPage(store: EditorStore, viewport: { width: number; height: number }) {
  const state = store.getState();
  const origin = pageOrigin(state.doc, state.pageIndex);
  const size = pageSize(state.doc, state.doc.pages[state.pageIndex]);
  const left = state.pan.x + origin.x * state.zoom;
  const top = state.pan.y + origin.y * state.zoom;
  const right = left + size.width * state.zoom;
  const bottom = top + size.height * state.zoom;
  const visible = left >= 0 && top >= 0 && right <= viewport.width && bottom <= viewport.height;
  if (visible) return;
  // Centre it, zooming out only when it does not fit.
  const zoom = Math.min(
    state.zoom,
    (viewport.width - PAD * 2) / size.width,
    (viewport.height - PAD * 2) / size.height,
  );
  state.setView({
    zoom,
    pan: {
      x: (viewport.width - size.width * zoom) / 2 - origin.x * zoom,
      y: (viewport.height - size.height * zoom) / 2 - origin.y * zoom,
    },
  });
}

/** Pan speed relative to the raw wheel/trackpad delta. */
const PAN_GAIN = 1.8;

export function zoomAt(store: EditorStore, factor: number, anchor: Point) {
  const { zoom, pan } = store.getState();
  const next = Math.max(0.02, Math.min(32, zoom * factor));
  const world = { x: (anchor.x - pan.x) / zoom, y: (anchor.y - pan.y) / zoom };
  store
    .getState()
    .setView({ zoom: next, pan: { x: anchor.x - world.x * next, y: anchor.y - world.y * next } });
}

export function Canvas({ viewportRef }: { viewportRef: React.RefObject<HTMLDivElement | null> }) {
  const store = useEditorApi();
  const env = useEnv();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const zoom = useEditor((s) => s.zoom);
  const pan = useEditor((s) => s.pan);
  const selection = useEditor((s) => s.selection);
  const hoverId = useEditor((s) => s.hoverId);
  const guides = useEditor((s) => s.guides);
  const editingTextId = useEditor((s) => s.editingTextId);
  const croppingId = useEditor((s) => s.croppingId);
  const readOnly = useEditor((s) => s.readOnly);
  const pageRef = useRef<HTMLDivElement>(null);
  const vision = useVisionMode();
  const gesture = useRef<Gesture | null>(null);
  const [marquee, setMarquee] = useState<Rect | null>(null);
  const [spaceDown, setSpaceDown] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const print = printMetrics(doc);
  const bleed = print?.bleedPx ?? 0;
  const page = doc.pages[pageIndex];
  const boardView = isBoardView(doc);
  const size = pageSize(doc, page);
  // biome-ignore lint/correctness/useExhaustiveDependencies: positions depend on the pages and the format only
  const rects = useMemo(
    () => (boardView ? boardRects(doc) : []),
    [boardView, doc.pages, doc.format],
  );
  const origin = pageOrigin(doc, pageIndex);
  const pageOffset = usePagePan();
  /** Elements are dragged: the active page lets them show beyond its edge (to the next page). */
  const [dragging, setDragging] = useState(false);
  const isVideo = doc.kind === "video";
  const videoApi = useVideoApi();
  const captionRows = useCaptions().rows;
  const commentUi = useCommentUi();
  const live = useLiveSync();

  const hiddenIds = useMemo(
    () => new Set([editingTextId, croppingId].filter((id): id is string => Boolean(id))),
    [editingTextId, croppingId],
  );

  // Fit the page when the editor opens or the format changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on format changes (on a board, single page sizes do not refit)
  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element) return;
    fitView(store, element.getBoundingClientRect());
  }, [
    store,
    viewportRef,
    boardView,
    boardView ? 0 : size.width,
    boardView ? 0 : size.height,
    bleed,
  ]);

  // Commands ask for a view: all pages, this page, or just bring it into view.
  const fitRequest = useEditor((s) => s.fitRequest);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when a new request arrives
  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element || !fitRequest) return;
    const rect = element.getBoundingClientRect();
    if (fitRequest.mode === "focus") focusPage(store, rect);
    else fitView(store, rect, fitRequest.mode);
  }, [fitRequest?.id]);

  // Keep text box heights in sync with their content (auto height).
  useLayoutEffect(() => {
    const root = pageRef.current;
    if (!root || store.getState().gestureBase) return;
    const updates: [string, number][] = [];
    for (const node of root.querySelectorAll<HTMLElement>("[data-element-id]")) {
      const id = node.dataset.elementId!;
      const element = doc.elements[id];
      const auto = element?.type === "table" || (element?.type === "text" && element.autoHeight);
      if (!auto) continue;
      const content = node.querySelector<HTMLElement>("[data-text-content]");
      if (!content) continue;
      const height = Math.max(1, Math.ceil(content.offsetHeight));
      if (Math.abs(height - element.height) > 0.5) updates.push([id, height]);
    }
    if (updates.length > 0) {
      store.getState().patch((draft) => {
        for (const [id, height] of updates) {
          const element = draft.elements[id];
          if (element?.type !== "text" && element?.type !== "table") continue;
          // Grow downwards, keeping the top edge in place (also when rotated).
          const delta = height - element.height;
          const angle = (element.rotation * Math.PI) / 180;
          element.x += (-Math.sin(angle) * delta) / 2;
          element.y += (Math.cos(angle) * delta) / 2 - delta / 2;
          element.height = height;
        }
      });
    }
  });

  // Re-measure when web fonts finish loading.
  const [, setFontTick] = useState(0);
  useEffect(() => {
    const onLoad = () => setFontTick((tick) => tick + 1);
    document.fonts.addEventListener("loadingdone", onLoad);
    return () => document.fonts.removeEventListener("loadingdone", onLoad);
  }, []);

  // Space bar for panning
  useEffect(() => {
    const isTyping = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
    // In videos the space bar plays and pauses.
    if (isVideo) return;
    const down = (event: KeyboardEvent) => {
      if (event.code === "Space" && !isTyping(event.target)) {
        event.preventDefault();
        setSpaceDown(true);
      }
    };
    const up = (event: KeyboardEvent) => {
      if (event.code === "Space") setSpaceDown(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [isVideo]);

  // Wheel: pinch / ctrl = zoom, otherwise pan. Deltas are collected and applied once per
  // frame, so a fast trackpad (several events per frame) does not render more than needed.
  const worldRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;
    let frame = 0;
    let settle = 0;
    let panX = 0;
    let panY = 0;
    let zoomLog = 0;
    let anchor: Point = { x: 0, y: 0 };
    const flush = () => {
      frame = 0;
      if (zoomLog !== 0) {
        zoomAt(store, Math.exp(zoomLog), anchor);
        zoomLog = 0;
      }
      if (panX !== 0 || panY !== 0) {
        const { pan: current } = store.getState();
        store.getState().setView({ pan: { x: current.x - panX, y: current.y - panY } });
        panX = 0;
        panY = 0;
      }
    };
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      // The framing overlay zooms the clip picture instead.
      if ((event.target as HTMLElement | null)?.closest?.("[data-framing]")) return;
      const rect = element.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        zoomLog -= event.deltaY * (event.deltaMode === 1 ? 0.05 : 0.0025);
        anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      } else {
        // Trackpad deltas are small; a gain (like Figma/Miro) makes a swipe cover more canvas.
        const scale = event.deltaMode === 1 ? 24 : PAN_GAIN;
        panX += event.deltaX * scale;
        panY += event.deltaY * scale;
      }
      // Own compositor layer while moving; dropped afterwards so the page is crisp again.
      if (worldRef.current) worldRef.current.style.willChange = "transform";
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        if (worldRef.current) worldRef.current.style.willChange = "";
      }, 150);
      if (!frame) frame = requestAnimationFrame(flush);
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", onWheel);
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
    };
  }, [store, viewportRef]);

  const toWorld = useCallback(
    (clientX: number, clientY: number): Point => {
      const rect = viewportRef.current!.getBoundingClientRect();
      const state = store.getState();
      const p = pagePan(state);
      return {
        x: (clientX - rect.left - p.x) / state.zoom,
        y: (clientY - rect.top - p.y) / state.zoom,
      };
    },
    [store, viewportRef],
  );

  /** Pointer position on the board (without a board the same as `toWorld`). */
  const toBoard = useCallback(
    (clientX: number, clientY: number): Point => {
      const rect = viewportRef.current!.getBoundingClientRect();
      const { zoom: z, pan: p } = store.getState();
      return { x: (clientX - rect.left - p.x) / z, y: (clientY - rect.top - p.y) / z };
    },
    [store, viewportRef],
  );

  const hitElement = useCallback(
    (clientX: number, clientY: number): string | null => {
      const state = store.getState();
      const parents = buildParentMap(state.doc);
      // Just switched pages: the DOM still shows the previous one, so measure instead.
      const shown =
        pageRef.current?.querySelector<HTMLElement>("[data-scene-page]")?.dataset.scenePage;
      const activePage = state.doc.pages[state.pageIndex];
      if (activePage && shown !== undefined && shown !== activePage.id) {
        const point = toWorld(clientX, clientY);
        for (const id of [...activePage.children].reverse()) {
          const element = state.doc.elements[id];
          const box = element && !element.hidden ? worldBox(state.doc, id, parents) : null;
          if (box && pointInBox(point, box)) return id;
        }
        return null;
      }
      for (const node of document.elementsFromPoint(clientX, clientY)) {
        if (!pageRef.current?.contains(node)) continue;
        const target = (node as HTMLElement).closest<HTMLElement>("[data-element-id]");
        if (!target) continue;
        const id = target.dataset.elementId!;
        // Prefer an already selected ancestor (e.g. an element picked in the layers panel).
        let current: string | undefined = id;
        while (current) {
          if (state.selection.includes(current)) return current;
          const parent = parents.get(current);
          current = parent?.kind === "group" ? parent.groupId : undefined;
        }
        return rootOf(id, parents);
      }
      return null;
    },
    [store, toWorld],
  );

  const snapTargets = useCallback(
    (exclude: string[]) => {
      const state = store.getState();
      const { guides, grid } = useViewPrefs.getState();
      return collectTargets(state.doc, state.pageIndex, new Set(exclude), printMetrics(state.doc), {
        guides,
        grid,
      });
    },
    [store],
  );

  const startMove = (event: ReactPointerEvent, clickId: string | null) => {
    const state = store.getState();
    const parents = buildParentMap(state.doc);
    const ids = state.selection.filter((id) => !state.doc.elements[id]?.locked);
    const items = ids.flatMap((id) => {
      const box = worldBox(state.doc, id, parents);
      return box ? [{ id, box }] : [];
    });
    const rect = selectionRect(state.doc, ids);
    if (!rect || state.readOnly) {
      gesture.current = null;
      return;
    }
    gesture.current = {
      type: "move",
      start: toWorld(event.clientX, event.clientY),
      items,
      rect,
      targets: snapTargets(ids),
      moved: false,
      clickId,
    };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button === 2) return;
    let state = store.getState();
    // Comment mode: the click puts a pin on the page instead of selecting.
    if (commentUi?.placing && event.button === 0 && !spaceDown) {
      const point = toWorld(event.clientX, event.clientY);
      const current = state.doc.pages[state.pageIndex];
      if (current) {
        commentUi.setDraft({
          pageId: current.id,
          x: Math.round(point.x),
          y: Math.round(point.y),
          ...(videoApi ? { timeMs: Math.round(videoApi.getState().time) } : {}),
        });
        commentUi.setPlacing(false);
      }
      return;
    }
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    if (event.button === 1 || spaceDown) {
      gesture.current = {
        type: "pan",
        start: { x: event.clientX, y: event.clientY },
        pan: state.pan,
      };
      return;
    }
    if (state.editingTextId) state.setEditingText(null);
    if (state.croppingId) state.setCropping(null);
    if (isBoardView(state.doc)) {
      const label = (event.target as HTMLElement).closest<HTMLElement>("[data-page-label]");
      const board = toBoard(event.clientX, event.clientY);
      const all = boardRects(state.doc);
      const labelIndex = label ? Number(label.dataset.pageLabel) : -1;
      if (labelIndex >= 0 && labelIndex < all.length) {
        // The label of a page selects it; dragging it moves the page on the board.
        if (labelIndex !== state.pageIndex) state.setPage(labelIndex);
        else state.select([]);
        const rect = all[labelIndex]!;
        gesture.current = state.readOnly
          ? null
          : {
              type: "page-move",
              index: labelIndex,
              start: board,
              origin: { x: rect.x, y: rect.y },
              others: all.filter((_, index) => index !== labelIndex),
              moved: false,
            };
        return;
      }
      const at = pageAtBoardPoint(all, board);
      if (at >= 0 && at !== state.pageIndex) {
        // A click on another page makes it the active one and goes on there.
        state.setPage(at);
        state = store.getState();
      }
    }
    const point = toWorld(event.clientX, event.clientY);
    // Dragging inside the current selection moves it, even if something lies on top.
    if (state.selection.length > 0 && !event.shiftKey) {
      const parents = buildParentMap(state.doc);
      const inside = state.selection.some((id) => {
        const box = worldBox(state.doc, id, parents);
        return box ? pointInBox(point, box) : false;
      });
      if (inside) {
        startMove(event, hitElement(event.clientX, event.clientY));
        return;
      }
    }
    const hit = hitElement(event.clientX, event.clientY);
    if (hit) {
      if (event.shiftKey) {
        state.select([hit], "toggle");
        gesture.current = null;
        return;
      }
      if (!state.selection.includes(hit)) state.select([hit]);
      startMove(event, hit);
      return;
    }
    // Below the elements: the clips of the upper video tracks. Only a selected one moves
    // on drag; otherwise a drag still draws a selection frame (a full-frame B-roll would
    // cover every free spot) and a plain click selects the clip.
    const trackClip =
      videoApi && !event.shiftKey
        ? overlayClipAtPoint(state.doc, videoApi.getState().time, point)
        : null;
    const selectedClip = videoApi?.getState().selectedClip;
    if (trackClip && selectedClip?.track === "overlay" && selectedClip.id === trackClip.id) {
      gesture.current = state.readOnly
        ? null
        : {
            type: "track-move",
            track: "overlay",
            id: trackClip.id,
            start: point,
            box: { ...trackClip.box },
            moved: false,
          };
      return;
    }
    // A selected main track clip in a split screen moves with its box.
    const boxedMain =
      videoApi && selectedClip?.track === "video" && !trackClip
        ? boxedMainClipAtPoint(state.doc, videoApi.getState().time, point)
        : null;
    if (boxedMain?.box && boxedMain.id === selectedClip?.id) {
      gesture.current = state.readOnly
        ? null
        : {
            type: "track-move",
            track: "video",
            id: boxedMain.id,
            start: point,
            box: { ...boxedMain.box },
            moved: false,
          };
      return;
    }
    if (!event.shiftKey) {
      state.select([]);
      if (selectedClip?.track === "overlay") videoApi?.getState().selectClip(null);
    }
    gesture.current = {
      type: "marquee",
      start: point,
      current: point,
      additive: event.shiftKey,
      base: event.shiftKey ? state.selection : [],
      ...(trackClip ? { trackClip: trackClip.id } : {}),
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    const state = store.getState();
    if (live) {
      const at = toWorld(event.clientX, event.clientY);
      live.setPresence({ cursor: { x: Math.round(at.x), y: Math.round(at.y) } });
    }
    if (!g) {
      if (event.buttons === 0) state.setHover(hitElement(event.clientX, event.clientY));
      return;
    }
    if (g.type === "pan") {
      state.setView({
        pan: { x: g.pan.x + event.clientX - g.start.x, y: g.pan.y + event.clientY - g.start.y },
      });
      return;
    }
    const point = toWorld(event.clientX, event.clientY);
    const threshold = SNAP_PX / state.zoom;
    const snapping = !(event.metaKey || event.ctrlKey);
    if (g.type === "page-move") {
      const board = toBoard(event.clientX, event.clientY);
      let x = g.origin.x + board.x - g.start.x;
      let y = g.origin.y + board.y - g.start.y;
      if (!g.moved && Math.hypot(x - g.origin.x, y - g.origin.y) * state.zoom < 3) return;
      if (!g.moved) {
        g.moved = true;
        state.beginGesture();
      }
      if (event.shiftKey) {
        if (Math.abs(x - g.origin.x) > Math.abs(y - g.origin.y)) y = g.origin.y;
        else x = g.origin.x;
      }
      if (snapping) {
        const own = pageSize(state.doc, state.doc.pages[g.index]);
        const snapped = snapPageRect(
          { x, y, width: own.width, height: own.height },
          g.others,
          threshold,
        );
        x += snapped.dx;
        y += snapped.dy;
      }
      movePageOnBoard(store, g.index, x, y);
      return;
    }
    if (g.type === "track-move") {
      const dx = point.x - g.start.x;
      const dy = point.y - g.start.y;
      if (!g.moved && Math.hypot(dx, dy) * state.zoom < 3) return;
      if (!g.moved) {
        g.moved = true;
        state.beginGesture();
      }
      const moved = snapping
        ? snapBoxToFrame(
            { ...g.box, x: g.box.x + dx, y: g.box.y + dy },
            state.doc.format,
            threshold,
          )
        : { x: g.box.x + dx, y: g.box.y + dy };
      state.update((draft) => {
        const clip =
          g.track === "video"
            ? draft.timeline?.clips.find((item) => item.id === g.id)
            : draft.timeline?.overlays?.find((item) => item.id === g.id);
        if (clip?.box) clip.box = { ...clip.box, x: Math.round(moved.x), y: Math.round(moved.y) };
      });
      return;
    }
    if (g.type === "marquee") {
      g.current = point;
      const rect: Rect = {
        x: Math.min(g.start.x, point.x),
        y: Math.min(g.start.y, point.y),
        width: Math.abs(point.x - g.start.x),
        height: Math.abs(point.y - g.start.y),
      };
      setMarquee(rect);
      const pageChildren = state.doc.pages[state.pageIndex]?.children ?? [];
      const parents = buildParentMap(state.doc);
      const hits = pageChildren.filter((id) => {
        const element = state.doc.elements[id];
        const box = element && !element.hidden ? worldBox(state.doc, id, parents) : null;
        return box ? rectsIntersect(boundingRect(box), rect) : false;
      });
      state.select(g.additive ? [...new Set([...g.base, ...hits])] : hits);
      return;
    }
    if (g.type === "move") {
      let dx = point.x - g.start.x;
      let dy = point.y - g.start.y;
      if (!g.moved && Math.hypot(dx, dy) * state.zoom < 3) return;
      if (!g.moved) {
        g.moved = true;
        state.beginGesture();
        if (isBoardView(state.doc)) setDragging(true);
      }
      if (event.shiftKey) {
        if (Math.abs(dx) > Math.abs(dy)) dy = 0;
        else dx = 0;
      }
      let guides: ReturnType<typeof snapRect>["guides"] = [];
      if (snapping) {
        const snapped = snapRect(
          { ...g.rect, x: g.rect.x + dx, y: g.rect.y + dy },
          g.targets,
          threshold,
        );
        dx += snapped.dx;
        dy += snapped.dy;
        guides = snapped.guides;
      }
      state.update((draft) => {
        for (const item of g.items)
          setWorldBox(draft, item.id, { ...item.box, x: item.box.x + dx, y: item.box.y + dy });
      });
      state.setGuides(guides);
      return;
    }
    if (g.type === "resize") {
      if (!state.gestureBase) state.beginGesture();
      // The snapshot above was taken before the gesture started.
      const base = store.getState().gestureBase;
      if (!base) return;
      if (g.single) {
        const item = g.items[0]!;
        const element = base.elements[item.id];
        let target = point;
        let guides: ReturnType<typeof snapValue>["guides"] = [];
        const { sx, sy } = HANDLE_DIRECTIONS[g.handle];
        if (snapping && Math.abs(item.box.rotation % 360) < 0.01) {
          if (sx !== 0) {
            const snapped = snapValue(point.x, g.targets.x, threshold, "x", {
              from: item.box.y,
              to: item.box.y + item.box.height,
            });
            target = { ...target, x: snapped.value };
            guides = guides.concat(snapped.guides);
          }
          if (sy !== 0) {
            const snapped = snapValue(point.y, g.targets.y, threshold, "y", {
              from: item.box.x,
              to: item.box.x + item.box.width,
            });
            target = { ...target, y: snapped.value };
            guides = guides.concat(snapped.guides);
          }
        }
        const corner = sx !== 0 && sy !== 0;
        const aspect = corner && keepsAspect(element, false) !== event.shiftKey;
        const box = resizeBox(item.box, g.handle, target, {
          keepAspect: aspect,
          fromCenter: event.altKey,
        });
        state.update((draft) => {
          const current = draft.elements[item.id];
          if (!current) return;
          const original = base.elements[item.id]!;
          if (current.type === "text" && corner) {
            // Corners scale text including its font size.
            const factor = box.width / item.box.width;
            const base = original.type === "text" ? original : current;
            current.style.fontSize = Math.max(1, base.style.fontSize * factor);
            if (base.style.stroke && current.style.stroke)
              current.style.stroke.width = base.style.stroke.width * factor;
            if (base.marks && current.marks) {
              current.marks.forEach((mark, index) => {
                const size = base.marks?.[index]?.fontSize;
                if (size !== undefined) mark.fontSize = Math.max(1, size * factor);
              });
            }
          }
          if (current.type === "group") {
            // Scale children relative to the original group.
            const factor = box.width / item.box.width;
            const originalGroup = original.type === "group" ? original : current;
            for (const childId of originalGroup.children) resetSubtree(draft, base, childId);
            current.width = originalGroup.width;
            current.height = originalGroup.height;
            scaleContent(draft, item.id, factor);
          }
          if (current.type === "image" && current.crop && !corner) {
            const asset = env.assets.get(current.assetId);
            const originalCrop = original.type === "image" ? original.crop : null;
            if (asset?.width && asset.height && originalCrop) {
              // Side handles reveal more or less of the image instead of stretching it.
              const scale = item.box.width / (originalCrop.width * asset.width);
              const scaleY = item.box.height / (originalCrop.height * asset.height);
              const cw = Math.min(1, box.width / scale / asset.width);
              const ch = Math.min(1, box.height / scaleY / asset.height);
              const cx = sx < 0 ? originalCrop.x + originalCrop.width - cw : originalCrop.x;
              const cy = sy < 0 ? originalCrop.y + originalCrop.height - ch : originalCrop.y;
              current.crop = {
                x: Math.max(0, Math.min(1 - cw, cx)),
                y: Math.max(0, Math.min(1 - ch, cy)),
                width: cw,
                height: ch,
              };
            }
          }
          setWorldBox(draft, item.id, box);
        });
        state.setGuides(guides);
        return;
      }
      // Multi selection: uniform scaling of all elements.
      const { scale, anchor } = scaleRect(g.rect, g.handle, point);
      state.update((draft) => {
        for (const item of g.items) {
          const original = base.elements[item.id];
          if (!original) continue;
          resetSubtree(draft, base, item.id);
          scaleContent(draft, item.id, scale);
          const center = {
            x: item.box.x + item.box.width / 2,
            y: item.box.y + item.box.height / 2,
          };
          const nextCenter = {
            x: anchor.x + (center.x - anchor.x) * scale,
            y: anchor.y + (center.y - anchor.y) * scale,
          };
          const width = item.box.width * scale;
          const height = item.box.height * scale;
          setWorldBox(draft, item.id, {
            x: nextCenter.x - width / 2,
            y: nextCenter.y - height / 2,
            width,
            height,
            rotation: item.box.rotation,
          });
        }
      });
      return;
    }
    if (g.type === "rotate") {
      if (!state.gestureBase) state.beginGesture();
      const delta = angleFrom(g.center, point) - g.startAngle;
      state.update((draft) => {
        for (const item of g.items) {
          const center = {
            x: item.box.x + item.box.width / 2,
            y: item.box.y + item.box.height / 2,
          };
          const rotation =
            g.items.length === 1
              ? snapAngle(item.box.rotation + delta, event.shiftKey)
              : item.box.rotation + delta;
          const applied = rotation - item.box.rotation;
          const rad = (applied * Math.PI) / 180;
          const dx = center.x - g.center.x;
          const dy = center.y - g.center.y;
          const next = {
            x: g.center.x + dx * Math.cos(rad) - dy * Math.sin(rad),
            y: g.center.y + dx * Math.sin(rad) + dy * Math.cos(rad),
          };
          setWorldBox(draft, item.id, {
            ...item.box,
            x: next.x - item.box.width / 2,
            y: next.y - item.box.height / 2,
            rotation,
          });
        }
      });
    }
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    gesture.current = null;
    const state = store.getState();
    if (g?.type === "marquee") {
      setMarquee(null);
      const dragged = Math.hypot(g.current.x - g.start.x, g.current.y - g.start.y) * state.zoom;
      if (g.trackClip && dragged < 3) {
        state.select([]);
        videoApi?.getState().selectClip({ track: "overlay", id: g.trackClip });
      }
    }
    // Collage: an image dragged onto another cell of its group swaps the pictures.
    if (g?.type === "move" && g.moved && g.items.length === 1) {
      const swapWith = collageSwapTarget(
        state.doc,
        g.items[0]!.id,
        event.clientX,
        event.clientY,
        pageRef.current,
      );
      if (swapWith) {
        state.cancelGesture();
        swapCellImages(store, g.items[0]!.id, swapWith);
        state.setGuides([]);
        return;
      }
    }
    if (
      g?.type === "move" &&
      !g.moved &&
      g.clickId &&
      !event.shiftKey &&
      state.selection.length > 1
    ) {
      state.select([g.clickId]);
    }
    // Board: elements dropped on another page move over to it (one undo step with the drag).
    let crossed: { page: number; ids: string[] } | null = null;
    if (g?.type === "move" && g.moved && isBoardView(state.doc)) {
      const ids = g.items.map((item) => item.id);
      const rect = selectionRect(state.doc, ids);
      if (rect) {
        const here = pageOrigin(state.doc, state.pageIndex);
        const centre = {
          x: here.x + rect.x + rect.width / 2,
          y: here.y + rect.y + rect.height / 2,
        };
        const all = boardRects(state.doc);
        const target = pageAtBoardPoint(all, centre);
        if (target >= 0 && target !== state.pageIndex) {
          const there = all[target]!;
          state.update((draft) =>
            moveToPage(draft, ids, target, { x: here.x - there.x, y: here.y - there.y }),
          );
          crossed = { page: target, ids };
        }
      }
    }
    setDragging(false);
    state.endGesture();
    state.setGuides([]);
    if (crossed) {
      state.setPage(crossed.page);
      state.select(crossed.ids);
    }
  };

  const startHandle = (event: ReactPointerEvent, handle: Handle | "rotate") => {
    event.stopPropagation();
    const state = store.getState();
    if (state.readOnly) return;
    viewportRef.current?.setPointerCapture(event.pointerId);
    const parents = buildParentMap(state.doc);
    const ids = state.selection.filter((id) => !state.doc.elements[id]?.locked);
    const items = ids.flatMap((id) => {
      const box = worldBox(state.doc, id, parents);
      return box ? [{ id, box }] : [];
    });
    const rect = unionRects(items.map((item) => boundingRect(item.box)));
    if (!rect || items.length === 0) return;
    if (handle === "rotate") {
      const center =
        items.length === 1
          ? {
              x: items[0]!.box.x + items[0]!.box.width / 2,
              y: items[0]!.box.y + items[0]!.box.height / 2,
            }
          : { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
      gesture.current = {
        type: "rotate",
        center,
        startAngle: angleFrom(center, toWorld(event.clientX, event.clientY)),
        items,
      };
      return;
    }
    gesture.current = {
      type: "resize",
      handle,
      items,
      rect,
      targets: snapTargets(ids),
      single: items.length === 1,
    };
  };

  const onDoubleClick = (event: React.MouseEvent) => {
    const state = store.getState();
    if (state.readOnly) return;
    const hit = hitElement(event.clientX, event.clientY);
    if (!hit) return;
    const element = state.doc.elements[hit];
    if (element?.type === "text") state.setEditingText(hit);
    else if (element?.type === "image") state.setCropping(hit);
    else if (element?.type === "captions" && videoApi) {
      // Correct the caption on screen (or the next one) in the inspector.
      const t = videoApi.getState().time;
      const row =
        captionRows.find((entry) => entry.page && t >= entry.page.start && t < entry.page.end) ??
        captionRows.find((entry) => entry.start >= t) ??
        captionRows[captionRows.length - 1];
      if (row) videoApi.getState().focusCaption(row.key, true);
      else videoApi.getState().setCaptionTab("text");
    } else if (element?.type === "group") {
      // Enter the group: select the child under the pointer.
      for (const node of document.elementsFromPoint(event.clientX, event.clientY)) {
        const id = (node as HTMLElement).closest<HTMLElement>("[data-element-id]")?.dataset
          .elementId;
        if (id && id !== hit && element.children.includes(id)) {
          state.select([id]);
          break;
        }
      }
    }
  };

  /** The innermost image or image placeholder under the pointer (also inside groups). */
  /**
   * The image or image placeholder at the pointer, also inside groups: only the
   * topmost element counts (not a photo hidden below a headline), and neither
   * it nor a group around it may be locked.
   */
  const imageTargetAt = (clientX: number, clientY: number) => {
    const doc = store.getState().doc;
    for (const node of document.elementsFromPoint(clientX, clientY)) {
      if (!pageRef.current?.contains(node)) continue;
      const id = (node as HTMLElement).closest<HTMLElement>("[data-element-id]")?.dataset.elementId;
      if (!id) continue;
      const element = doc.elements[id];
      if (!element || !acceptsImage(element)) return undefined;
      const parents = buildParentMap(doc);
      for (let current: string | undefined = id; current; ) {
        if (doc.elements[current]?.locked) return undefined;
        const parent = parents.get(current);
        current = parent?.kind === "group" ? parent.groupId : undefined;
      }
      return element;
    }
    return undefined;
  };

  const onContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    const state = store.getState();
    if (state.readOnly) return;
    const hit = hitElement(event.clientX, event.clientY);
    if (hit && !state.selection.includes(hit)) state.select([hit]);
    if (!hit) {
      const point = toWorld(event.clientX, event.clientY);
      const parents = buildParentMap(state.doc);
      const inside = state.selection.some((id) => {
        const box = worldBox(state.doc, id, parents);
        return box ? pointInBox(point, box) : false;
      });
      if (!inside) state.select([]);
    }
    setMenu({ x: event.clientX, y: event.clientY });
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDropActive(false);
    if (store.getState().readOnly) return;
    const at = toWorld(event.clientX, event.clientY);
    const target = hitElement(event.clientX, event.clientY);
    const nested = imageTargetAt(event.clientX, event.clientY);
    const hitTarget = target ? store.getState().doc.elements[target] : undefined;
    // Without an unlocked image at the pointer, a drop adds the picture as a new element.
    const targetElement = nested ?? (acceptsImage(hitTarget) ? undefined : hitTarget);
    const icon = parseIconDrag(event.dataTransfer.getData(ICON_DRAG_TYPE));
    if (icon) {
      void addIcon(store, icon.name, icon.weight, at);
      return;
    }
    const assetId = event.dataTransfer.getData("application/x-cva-asset");
    if (assetId) {
      const asset = env.assets.get(assetId);
      if (!asset) return;
      // A video dropped onto a mockup or an image placeholder plays there.
      if (
        isVideo &&
        videoApi &&
        asset.kind === "video" &&
        targetElement &&
        (videoIntoDevice(store, videoApi, targetElement.id, asset) ||
          videoIntoPlaceholder(store, videoApi, targetElement.id, asset))
      ) {
        return;
      }
      // Videos and music go onto the timeline.
      if (isVideo && videoApi && (asset.kind === "video" || asset.kind === "audio")) {
        addClipFromAsset(store, videoApi, asset);
        return;
      }
      if (
        acceptsImage(targetElement) &&
        asset.kind === "image" &&
        event.altKey === false &&
        targetElement?.locked === false
      ) {
        const previous = screenshotOf(targetElement);
        replaceImage(
          store,
          targetElement.id,
          asset.id,
          asset,
          previous ? env.assets.get(previous) : null,
        );
        store.getState().select([targetElement.id]);
      } else {
        addAsset(store, asset, at);
      }
      return;
    }
    const files = [...event.dataTransfer.files];
    if (files.length > 0) {
      env.uploadAndInsert(
        files,
        targetElement && acceptsImage(targetElement) && files.length === 1
          ? { replaceId: targetElement.id }
          : { at },
      );
    }
  };

  const selectionBoxes = useMemo(() => {
    const parents = buildParentMap(doc);
    return selection.flatMap((id) => {
      const box = worldBox(doc, id, parents);
      return box ? [{ id, box }] : [];
    });
  }, [doc, selection]);

  const hoverBox = useMemo(() => {
    if (!hoverId || selection.includes(hoverId)) return null;
    return worldBox(doc, hoverId);
  }, [doc, hoverId, selection]);

  const editingElement = editingTextId ? doc.elements[editingTextId] : undefined;
  const croppingElement = croppingId ? doc.elements[croppingId] : undefined;
  const cursor = spaceDown
    ? gesture.current?.type === "pan"
      ? "grabbing"
      : "grab"
    : commentUi?.placing
      ? "crosshair"
      : "default";

  if (!page) return null;
  return (
    <>
      <div
        ref={viewportRef}
        role="application"
        aria-label="Arbeitsfläche"
        className="relative h-full w-full touch-none overflow-hidden bg-zinc-200/70 select-none"
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => {
          store.getState().setHover(null);
          live?.setPresence({ cursor: null });
        }}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        onDragOver={(event) => {
          if (readOnly) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
          setDropActive(true);
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={onDrop}
        data-editor-canvas=""
      >
        <VisionOverlay />
        <div
          ref={worldRef}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
          }}
        >
          {boardView ? (
            <BoardLayer
              doc={doc}
              rects={rects}
              activeIndex={pageIndex}
              zoom={zoom}
              readOnly={readOnly}
              resolveAsset={env.resolveAsset}
              onAdd={() => addPageAfterLast(store)}
            />
          ) : null}
          <div style={{ position: "absolute", left: origin.x, top: origin.y }}>
            <div
              ref={pageRef}
              style={{
                position: "absolute",
                left: -bleed,
                top: -bleed,
                boxShadow: "0 1px 3px rgb(0 0 0 / 0.12), 0 8px 32px rgb(0 0 0 / 0.12)",
                filter: visionFilter(vision, Math.max(size.width, size.height) / 300),
              }}
            >
              <ActivePage
                video={isVideo}
                doc={doc}
                pageIndex={pageIndex}
                bleed={bleed}
                hiddenIds={hiddenIds}
                resolveAsset={env.resolveAsset}
                clip={!dragging}
              />
            </div>
            {readOnly ? null : <PlaceholderHints doc={doc} pageIndex={pageIndex} zoom={zoom} />}
            {isVideo ? (
              <>
                <SafeZones width={doc.format.width} height={doc.format.height} zoom={zoom} />
                <TrackClipBox zoom={zoom} />
                <FramingOverlay zoom={zoom} />
              </>
            ) : null}
            {print ? (
              <PrintGuides
                width={doc.format.width}
                height={doc.format.height}
                bleed={bleed}
                safe={print.safePx}
                zoom={zoom}
              />
            ) : null}
            <LayoutOverlay viewportRef={viewportRef} zoom={zoom} pan={pageOffset} />
            {hoverBox && !gesture.current ? <Outline box={hoverBox} zoom={zoom} /> : null}
            {!editingElement && !croppingElement ? (
              <SelectionOverlay
                boxes={selectionBoxes}
                zoom={zoom}
                onHandle={startHandle}
                readOnly={readOnly}
              />
            ) : null}
            {guides.map((guide, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: guides have no identity
                key={index}
                className="pointer-events-none absolute bg-pink-500"
                style={
                  guide.axis === "x"
                    ? {
                        left: guide.position - 0.5 / zoom,
                        top: guide.from,
                        width: 1 / zoom,
                        height: guide.to - guide.from,
                      }
                    : {
                        top: guide.position - 0.5 / zoom,
                        left: guide.from,
                        height: 1 / zoom,
                        width: guide.to - guide.from,
                      }
                }
              />
            ))}
            {marquee ? (
              <div
                className="pointer-events-none absolute border border-brand-500 bg-brand-500/10"
                style={{
                  left: marquee.x,
                  top: marquee.y,
                  width: marquee.width,
                  height: marquee.height,
                  borderWidth: 1 / zoom,
                }}
              />
            ) : null}
            <PeerCanvasLayer zoom={zoom} />
            <EditorCommentPins zoom={zoom} />
            {editingElement?.type === "text" ? <TextEditor element={editingElement} /> : null}
            {croppingElement?.type === "image" ? (
              <CropOverlay element={croppingElement} zoom={zoom} />
            ) : null}
          </div>
        </div>
        <DrawLayer viewportRef={viewportRef} />
        <Rulers viewportRef={viewportRef} />
        {dropActive ? (
          <div className="pointer-events-none absolute inset-3 rounded-xl border-2 border-dashed border-brand-500 bg-brand-500/5" />
        ) : null}
      </div>
      {/* Outside the canvas: events from the menu portal must not reach its pointer handlers. */}
      <CanvasContextMenu at={menu} onClose={() => setMenu(null)} />
      <GuideControls />
    </>
  );
}

/** "Drop an image here" on the image placeholders and empty device screens of the page (editor only). */
function PlaceholderHints({
  doc,
  pageIndex,
  zoom,
}: {
  doc: SceneDocument;
  pageIndex: number;
  zoom: number;
}) {
  const boxes = useMemo(() => {
    const page = doc.pages[pageIndex];
    if (!page) return [];
    const parents = buildParentMap(doc);
    const result: { id: string; box: Box; device: boolean }[] = [];
    const visit = (ids: readonly string[]) => {
      for (const id of ids) {
        const element = doc.elements[id];
        if (!element || element.hidden) continue;
        if (element.type === "group") visit(element.children);
        else if (isImagePlaceholder(element) || (element.type === "device" && !element.assetId)) {
          const box = worldBox(doc, id, parents);
          if (box) result.push({ id, box, device: element.type === "device" });
        }
      }
    };
    visit(page.children);
    return result;
  }, [doc, pageIndex]);
  return (
    <>
      {boxes.map(({ id, box, device }) => {
        const small = Math.min(box.width, box.height) * zoom < 120;
        return (
          <div
            key={id}
            className="pointer-events-none absolute flex items-center justify-center"
            style={{
              left: box.x,
              top: box.y,
              width: box.width,
              height: box.height,
              transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined,
            }}
          >
            <span
              className="flex flex-col items-center gap-1 rounded-lg bg-white/80 px-2.5 py-1.5 text-center text-xs font-medium whitespace-nowrap text-zinc-600 shadow-sm"
              style={{ transform: `scale(${1 / zoom})` }}
            >
              <ImagePlus className="size-4" />
              {small
                ? null
                : device
                  ? doc.kind === "video"
                    ? "Screenshot oder Video hierher ziehen"
                    : "Screenshot hierher ziehen"
                  : doc.kind === "video"
                    ? "Bild oder Video hierher ziehen"
                    : "Bild hierher ziehen"}
            </span>
          </div>
        );
      })}
    </>
  );
}

function Outline({ box, zoom }: { box: Box; zoom: number }) {
  return (
    <div
      className="pointer-events-none absolute"
      style={{
        left: box.x,
        top: box.y,
        width: box.width,
        height: box.height,
        transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined,
        outline: `${1.5 / zoom}px solid rgb(99 102 241 / 0.7)`,
      }}
    />
  );
}

/** Trim line, bleed area and safety margin of print documents. */
function PrintGuides({
  width,
  height,
  bleed,
  safe,
  zoom,
}: {
  width: number;
  height: number;
  bleed: number;
  safe: number;
  zoom: number;
}) {
  const line = 1 / zoom;
  return (
    <div
      className="pointer-events-none absolute"
      style={{ left: -bleed, top: -bleed, width: width + bleed * 2, height: height + bleed * 2 }}
    >
      {bleed > 0 ? (
        <>
          {/* Bleed zone, cut off by the print shop */}
          <div className="absolute inset-x-0 top-0 bg-white/45" style={{ height: bleed }} />
          <div className="absolute inset-x-0 bottom-0 bg-white/45" style={{ height: bleed }} />
          <div
            className="absolute left-0 bg-white/45"
            style={{ top: bleed, bottom: bleed, width: bleed }}
          />
          <div
            className="absolute right-0 bg-white/45"
            style={{ top: bleed, bottom: bleed, width: bleed }}
          />
        </>
      ) : null}
      <div
        className="absolute"
        style={{
          left: bleed,
          top: bleed,
          width,
          height,
          outline: `${line}px solid rgb(239 68 68 / 0.75)`,
        }}
      />
      {safe > 0 ? (
        <div
          className="absolute"
          style={{
            left: bleed + safe,
            top: bleed + safe,
            width: width - safe * 2,
            height: height - safe * 2,
            outline: `${line}px dashed rgb(14 165 233 / 0.8)`,
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Board: all pages side by side
// ---------------------------------------------------------------------------

/** Every element of the page (also in groups) is the same object as before. */
function pageUnchanged(before: SceneDocument, after: SceneDocument, index: number): boolean {
  const a = before.pages[index];
  const b = after.pages[index];
  if (a !== b || before.format !== after.format || before.kind !== after.kind) return false;
  if (!b) return true;
  const stack = [...b.children];
  while (stack.length > 0) {
    const id = stack.pop()!;
    const element = after.elements[id];
    if (before.elements[id] !== element) return false;
    if (element?.type === "group") stack.push(...element.children);
  }
  return true;
}

/** One inactive page of the board, redrawn only when its own content changes. */
/** The active page; memoised so panning and zooming (which only move the layer) skip it. */
const ActivePage = memo(function ActivePage({
  video,
  doc,
  pageIndex,
  bleed,
  hiddenIds,
  resolveAsset,
  clip,
}: {
  video: boolean;
  doc: SceneDocument;
  pageIndex: number;
  bleed: number;
  hiddenIds: ReadonlySet<string>;
  resolveAsset: ReturnType<typeof useEnv>["resolveAsset"];
  clip: boolean;
}) {
  return video ? (
    <VideoPageRenderer doc={doc} hiddenIds={hiddenIds} resolveAsset={resolveAsset} />
  ) : (
    <SceneRenderer
      document={doc}
      pageIndex={pageIndex}
      bleed={bleed}
      hiddenIds={hiddenIds}
      resolveAsset={resolveAsset}
      clip={clip}
    />
  );
});

const BoardPage = memo(
  function BoardPage({
    doc,
    index,
    resolveAsset,
  }: {
    doc: SceneDocument;
    index: number;
    resolveAsset: ReturnType<typeof useEnv>["resolveAsset"];
  }) {
    return (
      <SceneRenderer document={doc} pageIndex={index} resolveAsset={resolveAsset} animate={false} />
    );
  },
  (before, after) =>
    before.index === after.index &&
    before.resolveAsset === after.resolveAsset &&
    pageUnchanged(before.doc, after.doc, after.index),
);

function pageLabelText(doc: SceneDocument, index: number): string {
  const page = doc.pages[index]!;
  const size = pageSize(doc, page);
  return `${page.name?.trim() || `Seite ${index + 1}`} · ${size.width} × ${size.height}`;
}

/**
 * The pages of the board except the active one (it is drawn with its overlays by the
 * canvas itself), their labels (drag to move a page) and the "add page" button.
 */
function BoardLayer({
  doc,
  rects,
  activeIndex,
  zoom,
  readOnly,
  resolveAsset,
  onAdd,
}: {
  doc: SceneDocument;
  rects: Rect[];
  activeIndex: number;
  zoom: number;
  readOnly: boolean;
  resolveAsset: ReturnType<typeof useEnv>["resolveAsset"];
  onAdd: () => void;
}) {
  const last = rects[rects.length - 1];
  const addSize = 56 / zoom;
  return (
    <>
      {doc.pages.map((page, index) => {
        const rect = rects[index];
        if (!rect) return null;
        const active = index === activeIndex;
        return (
          <div key={page.id}>
            {active ? null : (
              <div
                className="pointer-events-none absolute"
                style={{
                  left: rect.x,
                  top: rect.y,
                  boxShadow: "0 1px 3px rgb(0 0 0 / 0.12), 0 8px 32px rgb(0 0 0 / 0.1)",
                }}
              >
                <BoardPage doc={doc} index={index} resolveAsset={resolveAsset} />
              </div>
            )}
            <div
              data-page-label={index}
              title={readOnly ? undefined : "Ziehen, um die Seite zu verschieben"}
              className={cn(
                "absolute truncate font-medium whitespace-nowrap select-none",
                active ? "text-brand-700" : "text-zinc-500 hover:text-zinc-800",
                readOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing",
              )}
              style={{
                left: rect.x,
                top: rect.y - 26 / zoom,
                maxWidth: rect.width,
                height: 22 / zoom,
                fontSize: 12 / zoom,
                lineHeight: `${22 / zoom}px`,
              }}
            >
              {pageLabelText(doc, index)}
            </div>
            {active ? (
              <div
                className="pointer-events-none absolute"
                style={{
                  left: rect.x,
                  top: rect.y,
                  width: rect.width,
                  height: rect.height,
                  outline: `${2 / zoom}px solid rgb(99 102 241 / 0.85)`,
                }}
              />
            ) : null}
          </div>
        );
      })}
      {readOnly || !last || doc.pages.length >= 100 ? null : (
        <button
          type="button"
          title="Seite hinzufügen"
          aria-label="Seite hinzufügen"
          // The canvas starts gestures on pointer down; a click here must not.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={onAdd}
          className="absolute flex items-center justify-center rounded-full border border-dashed border-zinc-400 bg-white/70 text-zinc-500 hover:border-brand-500 hover:text-brand-600"
          style={{
            left: last.x + last.width + 48 / zoom,
            top: last.y + last.height / 2 - addSize / 2,
            width: addSize,
            height: addSize,
            fontSize: 28 / zoom,
            lineHeight: 1,
          }}
        >
          +
        </button>
      )}
    </>
  );
}
